import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import _size from 'lodash/size'
import _compact from 'lodash/compact'
import _get from 'lodash/get'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { Map, fromJS } from 'immutable'
import { useLazyQuery } from '@apollo/client'
import { STORE_KEY_DETAIL_VIDEO_RELATED } from '../../../services/store-keys/index'
import {
  SCREEN_TYPE_DETAIL_VIDEO,
  createUpstreamContext,
  updateUpstreamContextFromObject,
} from '../../../services/upstream-context/index'
import { getPrimary } from '../../../services/languages/index'
import {
  getLocationType,
  createTitleGroupId,
  getFormattedContentData,
  CONTENT_IMAGE_LIST,
} from '../../../services/content/index'
import { TYPE_CONTENT_SERIES } from '../../../services/content-type/index'
import { GET_RELATED } from '../../../services/content/query'
import Row, { STYLES } from '../../Row.v2/index'
import RowV3 from '../../Row.v3/index'
import TileV2Graphql from '../../Tile.v2/Tile.v2Graphql/index'
import { get as getConfig } from '../../../config/index'
import { addNewRelicAction } from '../../../services/app/_newrelic'
import { getDetailPageUiLocationGQL } from '../utils'

const config = getConfig()
const rowV3Active = !!_get(config, ['features', 'rowV3'])

function DetailRelated (props) {
  const {
    auth,
    tabData = Map(),
    userLanguage,
    upstreamContext,
  } = props
  const contentType = tabData.get('contentType')
  const detailData = contentType === TYPE_CONTENT_SERIES ? tabData.get('featuredContent', Map()) || Map() : tabData
  const detailContentType = detailData.get('contentType')
  const detailContentId = detailData.get('contentId')
  const detailLocationType = getLocationType({ data: tabData })
  const uiLocation = getDetailPageUiLocationGQL(detailLocationType)
  const [getRelatedData, { data, loading }] = useLazyQuery(GET_RELATED)
  const relatedData = fromJS(_get(data, ['contentList', 'content', 0, 'contentRelated'], []) || [])
  const titleGroupId = detailData.get('titleGroupId') || createTitleGroupId(detailContentId, detailContentType)
  const titleGroupIds = []
  const adminTitle = 'Related Videos'
  const limit = 16
  const loadingData = loading || !relatedData.size
  // create a placeholder list of 8 (2 rows)
  const placeholderList = fromJS([
    { titleGroupId: '1-video' },
    { titleGroupId: '2-video' },
    { titleGroupId: '3-video' },
    { titleGroupId: '4-video' },
    { titleGroupId: '5-video' },
    { titleGroupId: '6-video' },
    { titleGroupId: '7-video' },
    { titleGroupId: '8-video' },
  ])

  if (titleGroupId) {
    titleGroupIds.push(titleGroupId)
  }

  useEffect(() => {
    if (_size(titleGroupIds)) {
      getRelatedData({
        variables: {
          language: userLanguage,
          titleGroupIds: _compact(titleGroupIds),
          imageType: CONTENT_IMAGE_LIST,
          limit,
        },
        context: {
          jwt: auth.get('jwt'),
        },
      }).catch((e) => {
        const networkError = e && e.networkError
        const httpStatus = networkError && networkError.statusCode
        addNewRelicAction('GetRelatedQueryFailed', {
          titleGroupId,
          errorMessage: e instanceof Error ? e.message : String(e),
          ...(httpStatus != null ? { httpStatus } : {}),
        })
      })
    }
  }, [titleGroupId, userLanguage])

  const renderItem = (item, params) => {
    const tileData = getFormattedContentData(item)

    return (
      <TileV2Graphql
        key={params.index}
        data={tileData}
        upstreamContext={updateUpstreamContextFromObject(
          upstreamContext,
          {
            contentType: tileData.get('contentType'),
            itemIndex: params.index,
            contentId: tileData.get('contentId'),
          },
        )}
        adminTitle={adminTitle}
        index={params.index}
        uiLocation={uiLocation}
      />
    )
  }

  if (rowV3Active) {
    const items = loadingData ? placeholderList : relatedData

    return (
      <div className="detail-related-graphql">
        <RowV3
          style={STYLES.S12345}
          asGrid={true}
        >
          {items.map((item, index) => renderItem(item, { index }))}
        </RowV3>
      </div>
    )
  }

  return (
    <div className="detail-related-graphql">
      <Row
        items={loadingData ? placeholderList : relatedData}
        upstreamContext={createUpstreamContext({
          storeKey: STORE_KEY_DETAIL_VIDEO_RELATED,
          screenType: SCREEN_TYPE_DETAIL_VIDEO,
          screenParam: tabData.get('contentId'),
        })}
        createAccessor={() => null}
        style={STYLES.S12345}
        disableScale={true}
        disableHover={false}
        static={false}
        asGrid={true}
      >
        {(item, tileData) => renderItem(item, tileData)}
      </Row>
    </div>
  )
}

DetailRelated.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  tabData: ImmutablePropTypes.map.isRequired,
  userLanguage: PropTypes.string.isRequired,
  upstreamContext: ImmutablePropTypes.map,
}

export default compose(
  connectRedux(
    (state) => {
      const {
        auth,
        user,
        upstreamContext,
      } = state

      return {
        auth,
        upstreamContext,
        userLanguage: getPrimary(user.getIn(['data', 'language'])),
      }
    },
  ),
)(DetailRelated)
